import React, { FC, useCallback, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ILoginForm } from "types/index";
import { LoginValidation } from "utils/validation";
import { useThunkDispatch } from "hooks/useThunkDispatch";
import { InputField } from "common/form/InputField";
import { login } from "appRedux/actions/user";
import { useSelector } from "react-redux";
import { authSelector } from "appRedux/selectors/user";
import { loginFailure } from "appRedux/reducers/userReducer";

export const LoginPage: FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginForm>({
    resolver: yupResolver(LoginValidation),
  });

  const { loader, error } = useSelector(authSelector);
  const dispatch = useThunkDispatch();

  useEffect(() => {
    dispatch(loginFailure(null));
  }, [dispatch]);

  const onSubmit: SubmitHandler<ILoginForm> = useCallback(
    (data) => {
      dispatch(login(data));
    },
    [dispatch]
  );

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-surface overflow-hidden">
      {/* Card */}
      <div className="relative w-full max-w-sm mx-4 animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="rounded-xl bg-ink px-5 py-3">
            <img
              alt="logo"
              src="/images/logo.png"
              className="w-36 h-auto object-contain"
            />
          </div>
        </div>

        {/* Form card */}
        <div className="card p-8 shadow-card-md">
          <h2 className="text-center text-lg font-semibold text-ink mb-1">
            Welcome back
          </h2>
          <p className="text-center text-xs text-ink-muted mb-6">
            Sign in to your account to continue
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-center">
              <p className="text-xs font-medium text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <InputField
              placeholder="Enter your email address"
              label="Email"
              register={register}
              errorMessage={errors.email?.message}
              type="email"
              required
              name="email"
            />
            <InputField
              type="password"
              placeholder="Enter your password"
              label="Password"
              required
              errorMessage={errors.password?.message}
              register={register}
              name="password"
            />

            <button
              type="submit"
              disabled={
                !!errors?.email?.message ||
                !!errors?.password?.message ||
                loader
              }
              className="btn-primary w-full mt-2 py-2.5 active:scale-[0.99]"
            >
              {loader ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Signing in…
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-ink-muted">
          &copy; {new Date().getFullYear()} Evo Dashboard. All rights reserved.
        </p>
      </div>
    </div>
  );
};
